// Attachment plumbing shared by the chat composers.
//
// The load-bearing reason both halves exist: the server only records a turn's
// files on the persisted message's metadata, which the client doesn't see until
// its next load. Without the optimistic copy the file a user just attached
// disappears from the very turn they're looking at.

import type { MessageAttachment } from './types';

type MessageAttachmentFields = {
	images?: string[];
	attachments?: MessageAttachment[];
};

// Resolves to null rather than rejecting when the file can't be read (moved,
// renamed, or permission revoked between picking and sending). Two reasons it
// must not throw: callers await this in front of the network send, so a
// rejection would discard the user's whole message — and the thumbnail is
// decoration anyway, since the file itself is uploaded from the same `File`
// object by the multipart POST regardless. Settling is what matters; without
// the error handlers the promise would hang and wedge the composer forever.
const readAsDataUrl = (file: File): Promise<string | null> =>
	new Promise(resolve => {
		const reader = new FileReader();
		reader.onload = () => resolve(reader.result as string);
		reader.onerror = () => {
			console.warn(`[attachments] could not preview ${file.name}`, reader.error);
			resolve(null);
		};
		reader.onabort = () => resolve(null);
	});

/**
 * Turn the composer's `File[]` into the fields a user message renders from:
 * images as inline thumbs, everything else as chips. Empty sets are omitted so
 * callers can spread the result straight into the message.
 */
export async function buildOptimisticAttachments(files?: File[]): Promise<MessageAttachmentFields> {
	const list = files ?? [];
	const imageFiles = list.filter(f => f.type.startsWith('image/'));
	const attachments = list
		.filter(f => !f.type.startsWith('image/'))
		.map(f => ({ name: f.name, mimeType: f.type || 'application/octet-stream', sizeBytes: f.size }));

	// Read in parallel — serialised, five 10MB images cost several hundred ms of
	// wall clock the browser is happy to overlap. Unreadable files drop out of
	// the previews; they still upload.
	const images = (await Promise.all(imageFiles.map(readAsDataUrl))).filter((url): url is string => url !== null);

	return { ...(images.length ? { images } : {}), ...(attachments.length ? { attachments } : {}) };
}

/**
 * Read the attachment fields back off a persisted message's metadata. Mirrors
 * `buildOptimisticAttachments`' shape so the reload path and the optimistic path
 * produce identical messages.
 */
export function readAttachmentMetadata(raw: unknown): MessageAttachmentFields {
	// `metadata` is a json column and should arrive already parsed; the string
	// branch is kept because the caller in WorkspaceChat has always tolerated one
	// and nothing has proven it unreachable.
	let parsed: unknown = raw;
	if (typeof raw === 'string') {
		try {
			parsed = JSON.parse(raw);
		} catch {
			// Unparseable metadata just means no attachments to draw; the rest of
			// the message still renders.
			return {};
		}
	}
	if (!parsed || typeof parsed !== 'object') return {};
	const { imageUrls, attachments } = parsed as { imageUrls?: string[]; attachments?: MessageAttachment[] };
	return { ...(imageUrls?.length ? { images: imageUrls } : {}), ...(attachments?.length ? { attachments } : {}) };
}
